import { useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { QRCodeSVG, QRCodeCanvas } from "qrcode.react";
import { QrCode, Copy, Download } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";

type StorefrontLink = { handle: string; url: string };

export function SellerQRCode() {
  const { toast } = useToast();
  const { user } = useAuth();
  const canvasWrapRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, isError } = useQuery<StorefrontLink>({
    queryKey: ["/api/seller/storefront-link"],
    queryFn: async () => {
      const res = await fetch("/api/seller/storefront-link", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load storefront link");
      return res.json();
    },
  });

  const url = data?.url ?? "";
  const profileImageUrl = user?.profileImageUrl ?? undefined;

  const imageSettings = profileImageUrl
    ? { src: profileImageUrl, height: 44, width: 44, excavate: true }
    : undefined;

  const copyLink = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: "Link copied", description: "Storefront link copied to clipboard." });
    } catch {
      toast({ title: "Couldn't copy", description: url, variant: "destructive" });
    }
  };

  const downloadQR = () => {
    const canvas = canvasWrapRef.current?.querySelector("canvas");
    if (!canvas) return;
    const dataUrl = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `stoop-storefront-${data?.handle ?? "qr"}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
          <QrCode className="h-5 w-5 text-primary" />
        </div>
        <div className="min-w-0">
          <CardTitle>Your storefront QR code</CardTitle>
          <CardDescription>
            Print this or add it to your booth. Anyone who scans it lands on your storefront and can
            buy in seconds, no app required.
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col items-center gap-4">
        {isLoading ? (
          <div className="h-[232px] w-[232px] animate-pulse rounded-2xl bg-muted" />
        ) : isError || !url ? (
          <p className="py-8 text-sm text-muted-foreground">
            Couldn't load your storefront link. Try again shortly.
          </p>
        ) : (
          <>
            <div className="rounded-2xl border bg-white p-6 shadow-sm">
              <QRCodeSVG
                value={url}
                size={200}
                level="H"
                bgColor="#ffffff"
                fgColor="#111111"
                imageSettings={imageSettings}
              />
            </div>

            <div className="flex w-full max-w-sm flex-col items-center gap-1">
              <span className="break-all text-center text-sm font-medium">{url}</span>
            </div>

            <div className="flex w-full max-w-sm flex-col gap-2 sm:flex-row">
              <Button variant="outline" className="flex-1" onClick={copyLink}>
                <Copy className="mr-2 h-4 w-4" />
                Copy link
              </Button>
              <Button variant="outline" className="flex-1" onClick={downloadQR}>
                <Download className="mr-2 h-4 w-4" />
                Download QR
              </Button>
            </div>

            <div ref={canvasWrapRef} className="hidden">
              <QRCodeCanvas
                value={url}
                size={1024}
                level="H"
                bgColor="#ffffff"
                fgColor="#111111"
                includeMargin
                imageSettings={
                  profileImageUrl
                    ? { src: profileImageUrl, height: 220, width: 220, excavate: true }
                    : undefined
                }
              />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
